import React from 'react'
import throttle from 'lodash.throttle'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { CircularProgress } from '@mui/material'
import { Send } from 'iconsax-react'

import PeerChatFilters from './PeerChatFilters'
import Update from './Update'
import AddConvoButton from '../../atoms/AddConvoButton'
import UserSearchModal from '../../molecules/ChatModals/UserSearchModal'
import TaskModal from '../../molecules/ChatModals/TaskModal'
import ConversationList from './ConversationList'
import QueueSkeleton from '../../molecules/QueueSkeleton'
import Search from './Search'
import UserResult from './UserResult'
import Spacer from '@SonarCX/components/atoms/Spacer'

import { Routes } from '@SonarCX/Routes'
import { findFilterID, getLatestEvent } from '@SonarCX/utilities'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { reformatLatestActivity } from '@SonarCX/providers/SocketProvider/helpers'
import { useNewConversationCount } from '@SonarCX/providers/NewConversationCountProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useConversationScroll } from '@SonarCX/providers/ConversationScrollProvider'
import { selectAlert } from '@SonarCX/slices/alert'
import {
  selectDMs,
  setSelected as setSelectedAction
} from '@SonarCX/slices/dms'
import { updateUsers } from '@SonarCX/slices/user'
import {
  useAppNotification,
  TYPES as NOTIFICATION_TYPES
} from '@SonarCX/providers/AppNotificationProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '@SonarCX/hooks/useDelayedUserSearch'
import useStyles from './styles'

const dateComparator = (a, b, newestFirst) => {
  const aFilter = findFilterID(a.owner_id, a.isGroup, a.session)
  const aLast = getLatestEvent(
    { ...a, latestActivity: a.latest_activity },
    aFilter,
    !newestFirst
  )

  const bFilter = findFilterID(b.owner_id, b.isGroup, b.session)
  const bLast = getLatestEvent(
    { ...b, latestActivity: b.latest_activity },
    bFilter,
    !newestFirst
  )

  if (!aLast && !bLast) {
    return 0
  } else if (!aLast) {
    return 1
  } else if (!bLast) {
    return -1
  }

  return +new Date(bLast.date) - +new Date(aLast.date)
}

const ownspaceComparator = (a, b) => {
  const isApersonalSpace = a.users.every(x => a.users[0] === x) && !a.isGroup
  const isBpersonalSpace = b.users.every(x => b.users[0] === x) && !b.isGroup

  if (!isApersonalSpace && !isBpersonalSpace) {
    return 0
  } else if (isApersonalSpace) {
    return -1
  } else if (isBpersonalSpace) {
    return 1
  }
}
// compares date by lastest message
export const newestDateComparator = (a, b) => dateComparator(a, b, true)

// compares date by oldest message
const oldestDateComparator = (a, b) => dateComparator(b, a, false)

const NEW_TASK = 'New Task'
const DIRECT_CHAT = 'Send Message To'

const Conversations = ({
  conversations,
  current,
  selectHandler,
  doneLoading,
  allAgents = [],
  handleCloseConversation = () => {}
}) => {
  const dispatch = useAppDispatch()

  const { triggerRecheck } = useNewConversationCount()
  const {
    peerChat: {
      addAgent,
      convoMap,
      sentMessage,
      resetSentMessage,
      addGroupSession,
      setNewPush,
      selectedFilter
    }
  } = useSocket()
  const { order, setOrder, updateQueue, setScrollTo } = useQueueState()
  const { currentUser } = useAgent()
  const { shouldScroll } = useConversationScroll()
  const showNotification = useAppNotification()
  const { allUsers } = useUserAPI()
  const { consentVisible } = useAppSelector(selectAlert)
  const { queueLoaded, chatType, dms } = useAppSelector(selectDMs)
  const hasPermission = useAccess()
  const { isSmall } = useBreakpoint()
  const { hasBasic } = useUserPermissions()
  const {
    search: userSearch,
    results: userResults,
    loading: loadingSearch
  } = useDelayedUserSearch(
    false,
    EXCLUDE_ME(() => currentUser()?.id)
  )

  // Get props from Dashboard / PeerChat
  const [sort, setSort] = React.useState('Newest')
  const [taskModalOpen, setTaskModalOpen] = React.useState(false)
  const [peerSearchOpen, setPeerSearchOpen] = React.useState(false)
  const [selected, setSelected] = React.useState(null)
  const [initialOrderObject, setInitialOrderObject] = React.useState([])
  const queueTop = React.useRef()
  const queueBottom = React.useRef()
  const ref = React.useRef(null)
  const [searchValue, setSearchValue] = React.useState('')
  const searching = React.useMemo(
    () => searchValue.trim().length > 0,
    [searchValue]
  )

  const options = React.useMemo(() => {
    const opts = {}

    if (hasPermission(PERMS.PeerFull) && hasBasic) {
      opts[DIRECT_CHAT] = Send
    }

    return opts
  }, [hasPermission, hasBasic])

  const classes = useStyles({
    smallMedia: isSmall,
    isConsentVisible: consentVisible
  })

  const all = React.useMemo(
    () =>
      Object.values(conversations).map(conv => {
        // Add latest activity to the block
        const orderObj = initialOrderObject.find(
          // @ts-ignore
          obj => obj.id === conv?.session
        )
        const latest_activity =
          orderObj?.latest_activity.map(reformatLatestActivity) || []
        // @ts-ignore

        return { ...conv, latest_activity }
      }),
    [conversations, initialOrderObject]
  )

  // Perform filtering when searching
  const [matchedConversations, matchedUsers] = React.useMemo(() => {
    if (!searching) {
      return [[], []]
    }

    const convos: Record<string, any> = {}

    const query = searchValue.toLocaleLowerCase()
    all.forEach(convo => {
      let nameMatches = (convo.name ?? '').toLocaleLowerCase().includes(query)
      if (convo.isGroup) {
        const hasUser = (convo.fullUsers ?? []).some(user =>
          user.name.toLocaleLowerCase().includes(query)
        )
        nameMatches = nameMatches || hasUser
      }

      const messagesMatch = (convo.messages ?? []).some(message =>
        (message.content ?? '').toLocaleLowerCase().includes(query)
      )

      if (nameMatches || messagesMatch) {
        convos[convo.session] = convo
      }
    })

    // Make sure users don't show up twice
    const filteredUsers = userResults.filter(user => {
      const hasConvo = user.id in convos
      const nameMatches = user.name.toLocaleLowerCase().includes(query)
      return !hasConvo && nameMatches
    })

    return [Object.values(convos).filter(Boolean), filteredUsers]
  }, [searching, all, searchValue, userResults])

  // Adding searched users to cache
  React.useEffect(() => {
    dispatch(updateUsers(userResults))
  }, [dispatch, userResults])

  // Filtering conversations to display
  React.useEffect(() => {
    let list = all

    switch (sort) {
      case 'Newest':
        list.sort(newestDateComparator)
        list.sort(ownspaceComparator)
        break
      case 'Oldest':
        list.sort(oldestDateComparator)
        list.sort(ownspaceComparator)
        break
      case 'Name (A-Z)':
        list.sort((a, b) =>
          (allUsers[a.users[0]]?.name || '')
            .toString()
            .localeCompare((allUsers[b.users[0]]?.name || '').toString())
        )
        list.sort(ownspaceComparator)
        break
      case 'Name (Z-A)':
        list.sort((a, b) =>
          (allUsers[b.users[0]]?.name || '')
            .toString()
            .localeCompare((allUsers[a.users[0]]?.name || '').toString())
        )
        list.sort(ownspaceComparator)
        break
      default:
        break
    }

    list = list.map(c => c.session)
    // Sets order, updates with new messages
    setOrder(list)
  }, [sort, all, setOrder, order, currentUser, allUsers])

  // Sets ordering
  React.useEffect(() => {
    // setting order with new object from api
    if (order?.length) {
      if (order[0]?.hasOwnProperty('id')) {
        setInitialOrderObject(order)
      }
    }
  }, [order, queueLoaded])

  // Makes the new conversation count update
  React.useEffect(() => {
    const list = ref.current
    if (!list) {
      return
    }

    let mounted = true
    const throttledScroll = throttle(() => {
      mounted && triggerRecheck()
    }, 100)

    list.addEventListener('scroll', throttledScroll)

    return () => {
      mounted = false
      list.removeEventListener('scroll', throttledScroll)
    }
  }, [all, triggerRecheck])

  // Trigger update check after changing sort
  React.useEffect(() => {
    let mounted = true

    setTimeout(() => mounted && triggerRecheck(), 500)

    return () => {
      mounted = false
    }
  }, [sort, triggerRecheck])

  const handleSelect = item => {
    if (item === NEW_TASK) {
      setTaskModalOpen(true)
    } else if (item === DIRECT_CHAT) {
      setPeerSearchOpen(true)
    }
  }

  // creating session and selecting it
  const createChat = React.useCallback(async () => {
    createdRef.current = true
    let sessionId = await addGroupSession(selected, currentUser())
    dispatch(setSelectedAction(sessionId))
    if (sessionId) {
      setScrollTo(sessionId)
      return sessionId
    }
  }, [selected, currentUser, dispatch, addGroupSession, setScrollTo])

  const createdRef = React.useRef(false)
  React.useEffect(() => {
    // from all, search for user ID and add to peer list or fetch timeline again
    if (!selected || !selected.length) {
      return
    }

    if (createdRef.current === true) {
      return
    }

    // solo peer chat
    if (selected.length === 1) {
      if (chatType === 'Private Chat') {
        const one = selected[0]
        const exists = order?.find(id => id === one.id)
        const convo = all.find(chat => chat.user?.id === one.id)

        // case for opening a session that's already in queue
        if (exists && convo) {
          selectHandler(convo.session)
        } else {
          // selecting a user and adding them to queue
          setOrder([...order, one.id])
          selectHandler(one.id)
          updateQueue(one.id, 'user')
        }
        // resetting selected state to choose another user
        setSelected([])

        return
      }
    }

    let mounted = true

    const handleCreate = async () => {
      // need to open chat

      const id = await createChat()

      if (id) {
        selectHandler(id)
        return
      }
    }

    handleCreate().then(() => {
      if (mounted) {
        createdRef.current = false
        // resetting selected state to choose another user
        setSelected([])
      }
    })

    return () => {
      mounted = false
    }
  }, [
    selected,
    all,
    order,
    selectHandler,
    setOrder,
    updateQueue,
    currentUser,
    addAgent,
    addGroupSession,
    setNewPush,
    dispatch,
    createChat,
    chatType
  ])

  // received message, check is user is in list
  React.useEffect(() => {
    const userToAdd = convoMap.current.keys().next().value

    // add convo to peer list if not already in it
    if (sentMessage && userToAdd) {
      const chatExists = all.find(chat => chat.session === userToAdd)
      const notYours = userToAdd !== currentUser()?.id

      // check to see if need to readd to DMs
      if (notYours && !chatExists) {
        // checking if group chat

        const msg = convoMap.current.get(userToAdd)

        const sentUser = msg.content.user
        addAgent(sentUser)
        setNewPush(old => [...old, msg.content.sessionId])
        showNotification({
          muteOn: Routes.PEER_CHAT,
          type: NOTIFICATION_TYPES.PEER,
          message: msg.content.body,
          id: msg.id,
          users: [sentUser.id],
          name: sentUser?.name,
          msg
        })
      }

      // adding solo chat to order
      if (!order.includes(userToAdd) && notYours) {
        const msg = convoMap.current.get(userToAdd)
        setNewPush(old => [...old, msg.content.user.id])
        updateQueue(userToAdd, 'user')
      }

      resetSentMessage(false)
      convoMap.current.delete(userToAdd)
    }
  }, [
    dms,
    convoMap,
    sentMessage,
    currentUser,
    order,
    setOrder,
    updateQueue,
    addAgent,
    resetSentMessage,
    all,
    showNotification,
    setNewPush
  ])

  // Scrolls if needed
  React.useEffect(() => {
    if (!shouldScroll) {
      return
    }

    let ref = queueTop
    if (sort === 'Oldest') {
      ref = queueBottom
    } else {
    }

    if (ref.current) {
      // @ts-ignore
      ref.current.scrollIntoView(false)
    }
  }, [shouldScroll, sort, all])

  const resetChatInfo = React.useCallback(() => {
    setPeerSearchOpen(false)
  }, [])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.topArea}>
          <PeerChatFilters updateSort={setSort}>
            {Object.keys(options).length > 0 && (
              <>
                <Spacer />

                <AddConvoButton options={options} handleSelect={handleSelect} />
              </>
            )}
          </PeerChatFilters>

          <Search
            value={searchValue}
            setValue={setSearchValue}
            search={userSearch}
          />
        </div>

        {doneLoading ? (
          all.length === 0 ? (
            <div className={classes.empty} style={{ color: 'gray' }}>
              {['Open', 'All Chats'].includes(selectedFilter)
                ? 'No chats have come in yet.'
                : 'No chats for this time period.'}
            </div>
          ) : (
            <div className={classes.conversations} ref={ref} id='conversations'>
              <div ref={queueTop} />

              <ConversationList
                all={searching ? matchedConversations : all}
                isDirect
                handleCloseConversation={handleCloseConversation}
                current={searching ? '' : current}
                selectHandler={(...args) => {
                  selectHandler(...args)
                  if (searching) {
                    setSearchValue('')
                  }
                }}
                sort={searching ? '' : sort}
                allAgents={allAgents}
              />

              {loadingSearch ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                matchedUsers.map(user => (
                  <UserResult
                    key={user.id}
                    user={user}
                    setSelected={setSelected}
                    setRef={createdRef}
                    resetSearch={() => setSearchValue('')}
                  />
                ))
              )}

              <div ref={queueBottom} />
            </div>
          )
        ) : (
          <div className={classes.conversations}>
            <div />

            {new Array(5).fill(0).map((_, i) => (
              <QueueSkeleton key={`queue-skel-${i}`} />
            ))}

            <div />
          </div>
        )}

        <div className={classes.archive}>
          <Update selectHandler={selectHandler} />
        </div>
      </div>

      {taskModalOpen && (
        // @ts-ignore
        <TaskModal
          open={taskModalOpen}
          onClose={() => setTaskModalOpen(false)}
          canEdit
          canUpdate
        />
      )}

      {peerSearchOpen && (
        // @ts-ignore
        <UserSearchModal
          open={peerSearchOpen}
          onClose={resetChatInfo}
          setSelected={setSelected}
          setRef={createdRef}
        />
      )}
    </>
  )
}

export default Conversations
